// src/utils/gtm.js
export const GTM_ID = 'GTM-NFXT8K';

export const trackPageView = (path, title) => {
  if (typeof window === 'undefined') return;

  window.dataLayer.push({
    event: 'pageview',
    page: {
      path,
      title
    }
  });

  // Mark that we've sent a pageview (for onRouteUpdate logic)
  window.initialPageviewSent = true;
};

const processQueue = () => {
  if (!window.dataLayerQueue || !window.dataLayerQueue.length) return;

  const originalPush = window.dataLayer.push;

  // Process in batches with delays to avoid overwhelming the browser
  const processNextBatch = (index, batchSize = 3) => {
    if (index >= window.dataLayerQueue.length) {
      window.dataLayerQueue = [];
      return;
    }

    // Process a small batch
    const endIndex = Math.min(index + batchSize, window.dataLayerQueue.length);
    for (let i = index; i < endIndex; i++) {
      originalPush.apply(window.dataLayer, window.dataLayerQueue[i]);
    }

    // Process next batch with a delay
    setTimeout(() => {
      processNextBatch(endIndex, batchSize);
    }, 100);
  };

  processNextBatch(0);
};


const loadGTM = () => {
  // Don't load if already loaded
  if (window.google_tag_manager || document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${GTM_ID}"]`)) {
    return;
  }

  // Create and append script directly - no additional delay needed
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;

  // Add error handling
  script.onerror = () => {
    console.warn('Failed to load Google Tag Manager');
  };

  // Process queue when script loads
  script.onload = () => {
    setTimeout(() => processQueue(), 2000)
  };
  document.head.appendChild(script);

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    // DOM already ready, wait a small amount of time
    setTimeout(() => trackPageView(window.location.pathname, document.title), 100);
  } else {
    // Wait for DOM ready event
    document.addEventListener('DOMContentLoaded', () => {
      trackPageView(window.location.pathname, document.title);
    });
  }
};



// Initialize data layer and set up deferred loading
export const initGTM = () => {
  if (typeof window === 'undefined') return;

  // Create data layer
  window.dataLayer = window.dataLayer || [];
  window.dataLayerQueue = [];

  // Store original push method
  const originalPush = window.dataLayer.push;

  // Override push to queue events until GTM loads
  window.dataLayer.push = function(...args) {
    if (window.google_tag_manager) {
      // GTM loaded, use normal push
      return originalPush.apply(window.dataLayer, args);
    } else {
      // GTM not loaded, queue event
      window.dataLayerQueue.push(args);
      return window.dataLayer.length;
    }
  };

  // Initialize with the required basic event
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js'
  });

  // Use requestIdleCallback to determine WHEN to load GTM
  if ('requestIdleCallback' in window) {
    // Wait even longer - use a timeout of 5 seconds
    window.requestIdleCallback(() => {
      // Further delay loading until well after page load
      setTimeout(loadGTM, 3000);
    }, { timeout: 5000 });
  } else {
    // Fallback that waits for load plus a significant delay
    window.addEventListener('load', () => {
      setTimeout(loadGTM, 3000);
    });
  }
};


// Track critical events - forcing GTM to load if needed
export const trackCriticalEvent = (eventName, data = {}) => {
  if (typeof window === 'undefined') return;

  const criticalEvents = ['purchase', 'lead', 'signup'];

  // For critical events, load GTM immediately if not loaded
  if (criticalEvents.includes(eventName) && !window.google_tag_manager) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;

    script.onload = () => {
      processQueue();
      // Push the critical event
      window.dataLayer.push({
        event: eventName,
        ...data
      });
    };

    // Place in head for immediate loading of critical events
    document.head.appendChild(script);
  } else {
    // Push normally
    window.dataLayer.push({
      event: eventName,
      ...data
    });
  }
};

